import { useEffect, useState, type FormEvent } from 'react';
import { post } from '../lib/api.ts';
import { Button } from './ui/index.ts';
import { cx } from '../lib/cx.ts';

const CALL_TYPES = [
  { value: 'discovery', label: 'Discovery' },
  { value: 'assessment_review', label: 'Assessment review' },
  { value: 'crm_exploration', label: 'CRM exploration' },
  { value: 'kickoff', label: 'Kickoff' },
  { value: 'one_on_one', label: '1-on-1' },
  { value: 'renewal', label: 'Renewal' },
  { value: 'other', label: 'Other' },
];

const STATUSES = [
  { value: 'ran', label: 'Ran' },
  { value: 'no_show', label: 'No-show' },
  { value: 'rescheduled', label: 'Rescheduled' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'cancelled', label: 'Cancelled' },
];

/** `2026-08-10T14:05` in local time, which is what datetime-local expects. */
function nowLocal(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 16);
}

/**
 * The manual call logging form. Posting here writes the call and its
 * call_booked / call_ran stage events in one transaction, so a manually
 * logged call lands in the funnel exactly like an imported one.
 */
export function LogCallDialog({
  leadId,
  onClose,
  onSaved,
}: {
  leadId: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [callType, setCallType] = useState('discovery');
  const [status, setStatus] = useState('ran');
  const [occurredAt, setOccurredAt] = useState(nowLocal());
  const [minutes, setMinutes] = useState('');
  const [nextEventOwned, setNextEventOwned] = useState(false);
  const [nextEventDesc, setNextEventDesc] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /**
   * The checkbox and the description cannot disagree.
   *
   * They could before: a description with the box unchecked saved
   * next_event_owned = false alongside a real committed next step, and the
   * timeline then showed "no next event owned" directly above "Next: send
   * the brief to Adam". Next event owned % (SPEC 3.6) is the number that
   * scores the operator, so a contradiction there is worse than useless.
   */
  function describeNextEvent(value: string) {
    setNextEventDesc(value);
    if (value.trim() !== '') setNextEventOwned(true);
  }

  function toggleOwned(checked: boolean) {
    setNextEventOwned(checked);
    if (!checked) setNextEventDesc('');
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);

    const at = new Date(occurredAt);
    const parsedMinutes = minutes.trim() === '' ? null : Number(minutes);

    try {
      await post('/calls', {
        lead_id: leadId,
        call_type: callType,
        status,
        scheduled_at: at.toISOString(),
        // Only a call that ran has a ran_at; the API derives its stage
        // events from status, so this stays consistent either way.
        ran_at: status === 'ran' ? at.toISOString() : null,
        duration_seconds:
          parsedMinutes !== null && Number.isFinite(parsedMinutes)
            ? Math.round(parsedMinutes * 60)
            : null,
        next_event_owned: status === 'ran' ? nextEventOwned : null,
        next_event_desc: nextEventDesc.trim() === '' ? null : nextEventDesc.trim(),
      });
      onSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not save the call.');
    } finally {
      setSaving(false);
    }
  }

  const ran = status === 'ran';

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-canvas/70 p-6 pt-20 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-md rounded-lg border border-border bg-surface-overlay p-5 shadow-lg"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Log a call"
      >
        <h2 className="text-sm font-medium text-fg">Log a call</h2>
        <p className="mt-0.5 text-xs text-fg-subtle">
          Writes the call and its funnel events together.
        </p>

        <form onSubmit={onSubmit} className="mt-4 space-y-3.5">
          <Row label="Type">
            <Select value={callType} onChange={setCallType} options={CALL_TYPES} />
          </Row>

          <Row label="Outcome">
            <div className="flex flex-wrap gap-1">
              {STATUSES.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setStatus(option.value)}
                  className={cx(
                    'rounded-sm px-2 py-1 text-xs transition',
                    status === option.value
                      ? 'bg-accent-subtle text-accent-text'
                      : 'bg-surface-raised text-fg-subtle hover:text-fg',
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </Row>

          <Row label="When">
            <input
              type="datetime-local"
              value={occurredAt}
              onChange={(event) => setOccurredAt(event.target.value)}
              className="h-8 w-full rounded-md border border-border bg-surface px-2.5 text-sm text-fg focus:border-border-strong"
            />
          </Row>

          {ran && (
            <>
              <Row label="Minutes">
                <input
                  type="number"
                  min="0"
                  max="600"
                  value={minutes}
                  onChange={(event) => setMinutes(event.target.value)}
                  placeholder="optional"
                  className="numeric h-8 w-full rounded-md border border-border bg-surface px-2.5 text-sm text-fg placeholder:text-fg-faint focus:border-border-strong"
                />
              </Row>

              <Row label="Next event">
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm text-fg">
                    <input
                      type="checkbox"
                      checked={nextEventOwned}
                      onChange={(event) => toggleOwned(event.target.checked)}
                      className="size-3.5 accent-accent"
                    />
                    I own a specific next step
                  </label>
                  <input
                    value={nextEventDesc}
                    onChange={(event) => describeNextEvent(event.target.value)}
                    placeholder="Assessment to her by Thursday"
                    className="h-8 w-full rounded-md border border-border bg-surface px-2.5 text-sm text-fg placeholder:text-fg-faint focus:border-border-strong"
                  />
                  {!nextEventOwned && (
                    <p className="text-2xs text-risk">
                      Recorded as an open loop. Next event owned % is the number this feeds.
                    </p>
                  )}
                </div>
              </Row>
            </>
          )}

          {error && <p className="text-xs text-lost">{error}</p>}

          <div className="flex justify-end gap-2 border-t border-border-subtle pt-3">
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" size="sm" loading={saving}>
              Log call
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[5.5rem_1fr] items-start gap-3">
      <span className="pt-1.5 text-xs text-fg-subtle">{label}</span>
      {children}
    </div>
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-8 w-full rounded-md border border-border bg-surface px-2 text-sm text-fg focus:border-border-strong"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}
